import { useEffect, useState } from 'react';
import unitsService from '../services/units.service';
import type { OrderLeg } from '../services/orders.service';
import type { DriverWithMoves } from '../components/driver-status-monitor/types';
import { buildProEta, type LegEta } from '../components/driver-status-monitor/etaProjection';
import { useVisibilityAwareInterval } from './useVisibilityAwareInterval';

const EMPTY = { byLeg: new Map<number, LegEta>(), activeLegnum: null as number | null, loading: false };

export function useProEta(pronumber: number | null | undefined, legs: OrderLeg[] | undefined) {
  const [state, setState] = useState<typeof EMPTY & { forKey?: string }>(EMPTY);
  const [refresh, setRefresh] = useState(0);
  const hasOpenMoves = legs?.some(l => l.driver?.trim() && l.legstatus?.trim() !== 'DEST DEPARTED');
  useVisibilityAwareInterval(() => setRefresh(value => value + 1), hasOpenMoves ? 60_000 : null);

  // Refetch on assignment/status changes and while comparing a live open PRO.
  const legsKey = (legs ?? [])
    .map((l) => `${l.legnum}:${(l.driver ?? '').trim()}:${(l.legstatus ?? '').trim()}`)
    .join('|');

  useEffect(() => {
    const pro = Number(pronumber);
    if (!Number.isFinite(pro) || !legs?.length) { setState(EMPTY); return; }

    const units = Array.from(new Set(
      legs
        .filter((l) => (l.legstatus ?? '').trim() !== 'DEST DEPARTED' && (l.driver ?? '').trim() !== '')
        .map((l) => l.driver.trim()),
    ));
    if (units.length === 0) { setState(EMPTY); return; }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    Promise.all(units.map((u) =>
      unitsService.getDriver(u).then((r) => [u, r.data] as const).catch(() => [u, null] as const),
    )).then((results) => {
      if (cancelled) return;
      const driversByUnit = new Map<string, DriverWithMoves>();
      for (const [u, d] of results) if (d) driversByUnit.set(u, d);
      const { byLeg, activeLegnum } = buildProEta(pro, driversByUnit);
      setState({ byLeg, activeLegnum, loading: false, forKey: `${pronumber}:${legsKey}` });
    }).catch(() => { if (!cancelled) setState((s) => ({ ...s, loading: false })); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronumber, legsKey, refresh]);

  return state.forKey === `${pronumber}:${legsKey}` ? state : EMPTY;
}
